import React, { useEffect, useState } from "react";
import { Paper, Typography, List, ListItem, ListItemText, CircularProgress, Alert, Box, Divider } from "@mui/material";
import GroupsIcon from '@mui/icons-material/Groups';
import EmailIcon from '@mui/icons-material/Email';
import { getMyGroupProgress } from "../../-education/service/student-group-service";
import { GroupProgress } from "../../-education/model/stream";

export default function StudentGroupDashboard() {
    const [progress, setProgress] = useState<GroupProgress | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        getMyGroupProgress()
            .then((p: GroupProgress) => { setProgress(p); setLoading(false); })
            .catch(() => { setError("You are not currently assigned to any group."); setLoading(false); });
    }, []);

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="info">{error}</Alert>;
    if (!progress) return null;

    const unsubmitted = progress.totalBadges - progress.submittedBadges;

    return (
        <React.Fragment>
            <Paper sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <GroupsIcon color="primary" />
                    <Typography variant="h6">{progress.groupName}</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {progress.streamName && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Stream: {progress.streamName}
                    </Typography>
                )}
                {progress.groupEmail && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                            <a href={`mailto:${progress.groupEmail}`}>{progress.groupEmail}</a>
                        </Typography>
                    </Box>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">{progress.submittedBadges}</Typography>
                        <Typography variant="caption" color="text.secondary">Submitted</Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="warning.main">{unsubmitted}</Typography>
                        <Typography variant="caption" color="text.secondary">Unsubmitted</Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="info.main">{progress.evaluatedBadges}</Typography>
                        <Typography variant="caption" color="text.secondary">Evaluated</Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">{progress.totalBadges}</Typography>
                        <Typography variant="caption" color="text.secondary">Total Badges</Typography>
                    </Paper>
                </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Members ({progress.members.length})
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List disablePadding>
                    {progress.members.map((member) => (
                        <ListItem key={member.studentId} sx={{ px: 0, py: 1 }}>
                            <ListItemText
                                primary={member.studentName}
                                secondary={member.studentEmail}
                            />
                        </ListItem>
                    ))}
                </List>
            </Paper>
        </React.Fragment>
    );
}
